import React, { forwardRef, useMemo } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import useFileManager, { CompletedFile, CustomFile, FileManagerProps } from './useFileManager'
import { useTheme } from '@mui/material/styles'
import File_upload from '@tdcerhverv/parrotfish/dist/icons/Icons/File_upload.svg'
import Stop_media from '@tdcerhverv/parrotfish/dist/icons/Icons/Stop_media.svg'
import Play from '@tdcerhverv/parrotfish/dist/icons/Icons/Play.svg'
import Trash from '@tdcerhverv/parrotfish/dist/icons/Icons/Trash.svg'
import { TextInputProps } from '../TextInput/types'
import { useController } from 'react-hook-form'
import ErrorMessage from '../../ErrorMessage'
import FormHelperText from '@mui/material/FormHelperText'

type FileUpload = TextInputProps & {
  audioPlayback?: boolean
  fullWidth?: boolean
  maxNumberOfFiles?: number
  maxTotalFileSize?: number // in bytes, eg 20 MB = 20_000_000
  validateFile?: FileManagerProps['validateFile']
}

export type FileContent = {
  name: string
  content: string
  size: number
}

export type FileElement = {
  value: FileContent[]
}

const createComponentValue = (files: CustomFile[]): FileElement['value'] => {
  return files
    .filter(file => file.state === 'COMPLETED')
    .map(file => ({
      name: (file as CompletedFile).name,
      content: (file as CompletedFile).content,
      size: (file as CompletedFile).size,
    }))
}

const calculateTotalFileSize = (files: CustomFile[]): number =>
  files.reduce((totalSize, file) => totalSize + file.size, 0)

/**
 * @name FileUpload
 * @description Form to upload files. Accepts any file type and size out of the box, but also
 * supports restrictions on both in addition to upfront validation and built-in audio playback.
 *
 * @property {boolean} audioPlayback - Offer audio playback functionality. Default: false.
 * @property {boolean} fullWidth - Use full width of container, or use the default 400px. Default: false.
 * @property {number} maxNumberOfFiles - The maximum total number of files. This triggers validation error but doesn't stop further additions.
 * @property {number} maxTotalFileSize - Maximum Total File Size allowed for all files, in bytes, e.g. 20 mb = 20_000_000.
 * @property {function} validateFile - Upfront validation before the file is added to the list.
 */

export const FileUpload = forwardRef<FileElement, FileUpload>(function FileUpload(props, fieldRef) {
  const {
    audioPlayback = false,
    fullWidth = false,
    maxNumberOfFiles,
    maxTotalFileSize,
    validateFile,
    control,
    required,
    disabled,
    label,
    assistiveText,
    name,
    ...rest
  } = props

  const fieldName = props.name!
  const { field, fieldState } = useController({ name: fieldName, control, rules: { required: required! } })
  const hasError = Boolean(fieldState.error)
  const { onChange, ...fieldProps } = field

  const [audio, setAudio] = React.useState<HTMLAudioElement>()
  const [currentlyPlaying, setCurrentlyPlaying] = React.useState<CustomFile>()
  const [playedPercent, setPlayedPercent] = React.useState<number>()
  const { files, registerFile, unregisterFile } = useFileManager({
    validateFile,
  })
  const [isDragOver, setDragOver] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Handler for validating files.
  const validateState = React.useCallback(
    (currentFiles: CustomFile[]) => {
      if (maxNumberOfFiles && currentFiles.length > maxNumberOfFiles) {
        fileInputRef.current?.setCustomValidity('FILES_EXCEED_LIMIT')
      } else if (maxTotalFileSize && calculateTotalFileSize(currentFiles) >= maxTotalFileSize) {
        fileInputRef.current?.setCustomValidity('FILES_EXCEED_SIZE_LIMIT')
      } else {
        fileInputRef.current?.setCustomValidity('')
      }

      // Ensure that we show the error message if there have been a change
      fileInputRef.current?.checkValidity()
    },
    [fileInputRef, maxNumberOfFiles, maxTotalFileSize],
  )

  // Handler for adding files.
  const handleFileChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { currentTarget } = e
      const files = currentTarget.files || []
      const totalNumberOfFiles = files.length
      let numberOfCompletedFiles = 0

      // Mark the input as invalid, preventing the form from submitting
      // before the component is done reading the file
      fileInputRef.current?.setCustomValidity('READING_FILES')

      function handleFileCompleted(updatedFiles: CustomFile[]) {
        if (++numberOfCompletedFiles !== totalNumberOfFiles) return

        // Clear the validity
        fileInputRef.current?.setCustomValidity('')

        // trigger it with the updated list of files.
        onChange(createComponentValue(updatedFiles))

        // Ensure we are in a valid state
        validateState(updatedFiles)
      }

      function handleFileRejection(reason: string) {
        fileInputRef.current?.setCustomValidity(reason)

        // Ensure we are in a valid state
        fileInputRef.current?.checkValidity()
      }

      // For each of the files selected, register them and trigger `onChange`
      for (let i = 0; i < totalNumberOfFiles; i++) {
        const file = files[i]

        registerFile(file).then(handleFileCompleted, handleFileRejection)
      }

      // Create the content of the target
      currentTarget.value = ''
    },
    [registerFile, onChange],
  )

  React.useImperativeHandle(
    fieldRef,
    () => ({
      value: createComponentValue(files),
    }),
    [files],
  )

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(true)
  }
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
  }
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.files = e.dataTransfer.files
      fileInputRef.current.dispatchEvent(new Event('change', e))
      setDragOver(false)
    }
  }

  // Handler for audio start play
  const startAudioPlay = (file: CustomFile) => {
    if (file.state === 'COMPLETED' && /audio/.test(file.type)) {
      setCurrentlyPlaying(file)
      setAudio(new Audio(file.content))
    }
  }

  // Handler for audio stop play
  const stopAudioPlay = React.useCallback(() => {
    if (audio) {
      audio.pause()
      audio.currentTime = 0
      setCurrentlyPlaying(undefined)
      setAudio(undefined)
    }
  }, [audio])

  // Automate audio play and stop functions.
  React.useEffect(() => {
    if (audio) {
      audio.play()
      audio.onended = () => stopAudioPlay()
    }
  }, [audio, stopAudioPlay])

  // Playback progress handler
  const handlePlaybackProgress = React.useCallback(() => {
    if (!!currentlyPlaying && audio) {
      setPlayedPercent(Math.round((audio.currentTime * 100) / audio.duration))
    } else {
      setPlayedPercent(0)
    }
  }, [currentlyPlaying, audio])

  // Hook for tracking playback progress
  React.useEffect(() => {
    if (audio) {
      audio.addEventListener('timeupdate', handlePlaybackProgress)
    }
  }, [audio, handlePlaybackProgress])

  // Handler to removing file from list
  const handleRemove = React.useCallback(
    (file: CustomFile) => {
      currentlyPlaying === file && stopAudioPlay()

      unregisterFile(file).then(updatedFiles => {
        // If the consumer have provided an `onChange` handler we want to
        // trigger it with the updated list of files.
        if (typeof onChange === 'function') {
          onChange(createComponentValue(updatedFiles))
        }

        // Ensure we are in a valid state
        validateState(updatedFiles)
      })
    },
    [currentlyPlaying, unregisterFile, onChange, stopAudioPlay],
  )

  const { spacing, palette, shape } = useTheme()
  const styles = useMemo(
    () => ({
      root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxWidth: fullWidth ? null : '400px',
        pb: '24px',
      },
      inputWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '16px',
        '& p, span': {
          color: hasError ? palette.error.main : 'inherit',
        },
      },
      paper: {
        position: 'relative',
        borderColor: hasError ? palette.error.main : palette.grey[600],
        boxSizing: 'border-box',

        '&:not([data-disabled="true"])': {
          '&:hover, &:active, &:focus, &:focus-within, &[data-dragover="true"]': {
            borderStyle: 'dashed',
            backgroundColor: palette.grey[100],
          },
        },

        '&[data-disabled="true"]': {
          color: palette.grey[400],
          borderColor: palette.grey[400],
        },
      },
      fileInputElement: {
        position: 'absolute',
        opacity: 0,
        width: '100%',
        inset: 0,
      },
      fileType: {
        textTransform: 'uppercase',
        letterSpacing: 'normal',
        color: 'common.white',
        backgroundColor: palette.grey[800],
        borderRadius: `${shape.borderRadius / 2}px`,
        margin: spacing(0, 1.5, 0, 1),
        padding: spacing(0, 0.6),
      },
      upLoaded: {
        display: 'flex',
        flexDirection: 'column',
        gridRowGap: '16px',
        padding: '8px',
      },
      errorLabel: {
        margin: '8px 0 0 0',
      },
    }),
    [hasError],
  )

  return (
    <Box sx={styles.root}>
      <Paper
        data-dragover={isDragOver}
        data-disabled={disabled}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        variant="outlined"
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={styles.paper}
      >
        <Box sx={styles.inputWrapper}>
          <SvgIcon fontSize="medium" component={File_upload} viewBox="0 0 48 48" />
          <Box>
            <Typography sx={{ lineHeight: 1.3 }}>{label}</Typography>
            <Typography variant="caption" color={disabled ? 'inherit' : 'textSecondary'}>
              {assistiveText}
            </Typography>
          </Box>
        </Box>
        <input
          {...rest}
          {...fieldProps}
          value={fieldProps?.value?.name}
          onChange={handleFileChange}
          name={name}
          data-testid="fileInputElement"
          type="file"
          style={{
            position: 'absolute',
            opacity: 0,
            width: '100%',
            inset: 0,
            cursor: 'pointer',
          }}
          ref={fileInputRef}
          multiple={Number(maxNumberOfFiles) > 1}
        />
      </Paper>
      {hasError && required && (
        <FormHelperText error variant="standard" sx={styles.errorLabel}>
          <ErrorMessage>
            <>{fieldState.error?.message}</>
          </ErrorMessage>
        </FormHelperText>
      )}

      {!!files.length && (
        <Paper sx={{ border: `1px solid ${palette.grey[200]}` }}>
          <Box sx={styles.upLoaded}>
            {files.map((file, i) => {
              const isReady = file.state === 'COMPLETED'
              const isAudio = /audio/.test(file.type)
              const isPlaying = currentlyPlaying === file
              const anotherIsPlaying = !!currentlyPlaying && !isPlaying
              return (
                <Grid container direction="row" alignItems="center" wrap="nowrap" key={`${i}:${file.name}`}>
                  <Grid sx={{ width: 56, flexShrink: 0 }}>
                    <Typography variant="overline" sx={styles.fileType}>
                      {file.name.slice(file.name.lastIndexOf('.') + 1)}
                    </Typography>
                  </Grid>
                  <Grid sx={{ flexGrow: 1, wordBreak: 'break-word' }}>
                    <Typography variant="caption">{file.name}</Typography>
                  </Grid>
                  <Grid sx={{ flexShrink: 0 }}>
                    <Box display="flex" justifyContent="flex-end" alignItems="center" fontSize={16}>
                      {!isReady && <CircularProgress size={16} variant="indeterminate" />}
                      {isAudio && audioPlayback && isReady && (
                        <IconButton
                          aria-label="audio"
                          data-testid="audio"
                          disabled={anotherIsPlaying}
                          onClick={() => {
                            if (isPlaying) {
                              stopAudioPlay()
                            } else {
                              startAudioPlay(file)
                            }
                          }}
                          size="small"
                        >
                          {isPlaying && (
                            <>
                              <CircularProgress
                                size={32}
                                color="inherit"
                                variant="determinate"
                                value={100}
                                sx={{
                                  position: 'absolute',
                                  color: palette.grey[300],
                                }}
                              />
                              <CircularProgress
                                size={32}
                                color="primary"
                                variant="determinate"
                                value={playedPercent}
                                sx={{
                                  position: 'absolute',
                                }}
                              />
                            </>
                          )}
                          <SvgIcon component={isPlaying ? Stop_media : Play} fontSize="inherit" viewBox="0 0 48 48" />
                        </IconButton>
                      )}
                      <IconButton
                        aria-label="remove"
                        data-testid="remove"
                        onClick={() => handleRemove(file)}
                        size="small"
                      >
                        <SvgIcon component={Trash} fontSize="inherit" viewBox="0 0 48 48" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              )
            })}
          </Box>
        </Paper>
      )}
    </Box>
  )
})
